import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeForm,
  TeIcon,
  TeLabel,
  TeModal,
  TeMultiselect,
  TeMultiselectExtraOption,
  TeMultiselectOption,
  TeText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import {
  resetFormWithName,
  submitFormWithName,
} from '@topcon/tuicl/dist/components/form/form.utils';
import { useNotification } from '@topcon/topcon-utils';
import {
  LitMultiselect,
  TeMultiselectOptionChangeEvent,
  TeMultiselectPendingEvent,
  TeOptionAddEvent,
} from '@topcon/tuicl';

import { useCreateInviteToJoin } from '../userManagement.mutations';
import { useSuggestedIdentities } from './invitation.queries';
import ProfileSelector from '../../../components/ProfileSelector';
import { IdentityBody } from '../../../api/generated/pap';
import { EMAIL_PATTERN } from '../../../utils/utils';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

interface Props {
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

function InvitationModal({ open, onClose }: Props) {
  const suggestionMinCharacters = 3;
  const suggestionDebounceMs = 300;
  const [invitationEmails, setInvitationEmails] = useState<string[]>([]);
  const [invitationProfiles, setInvitationProfiles] = useState<string[]>([]);
  const [usersToInvite, setUsersToInvite] = useState<string[]>([]);
  const [pendingInput, setPendingInput] = useState<{ value: string; valid: boolean }>({
    value: '',
    valid: true,
  });
  const [debouncedSuggestionFilter, setDebouncedSuggestionFilter] = useState('');
  const multiselectRef = useRef<LitMultiselect>(null);
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { t } = useTranslation('users');
  const { mutateAsync: inviteToJoin, isPending: isInvitePending } = useCreateInviteToJoin();
  // TODO: Suggestions don't work right now from BE but once they do we should use them as options for multi select
  const { data: suggestions } = useSuggestedIdentities(debouncedSuggestionFilter);
  const shouldShowSuggestions = debouncedSuggestionFilter.trim().length >= suggestionMinCharacters;

  useEffect(() => {
    if (!open) {
      setPendingInput({ value: '', valid: true });
      setDebouncedSuggestionFilter('');
    }
  }, [open]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSuggestionFilter(pendingInput.value.trim());
    }, suggestionDebounceMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [suggestionDebounceMs, pendingInput.value]);

  const handlePendingChange = (event: TeMultiselectPendingEvent) => {
    setPendingInput({ value: event.value, valid: event.valid });
  };

  const handleInviteEmailAdd = (event: TeOptionAddEvent) => {
    setInvitationEmails((prev) => [...prev, event.option]);
  };

  const handleInviteEmailRemove = (e: TeMultiselectOptionChangeEvent) => {
    setInvitationEmails((prev) => prev.filter((s) => s !== e.value));
  };

  const resetInvitationModal = (shouldUpdate: boolean) => {
    onClose(shouldUpdate);
    setTimeout(() => {
      setInvitationProfiles([]);
      setInvitationEmails([]);
      setUsersToInvite([]);
      setPendingInput({ value: '', valid: true });
      resetFormWithName('newInvitationsForm');
    }, 200); // TODO: Move timeout to constant
  };

  const handleFormSubmit = async () => {
    const committed = multiselectRef.current?.commitPendingInput() ?? null;
    const allEmails = [
      ...new Set([...usersToInvite, ...invitationEmails, ...(committed ? [committed] : [])]),
    ];
    const profileIds = invitationProfiles;

    for await (const res of allEmails.map((email) =>
      inviteToJoin({
        email,
        profileIds,
      }).catch((error: unknown) => {
        if (showError(error)) {
          return;
        }
        notificationService.addNotification(
          t('usersInvitedFailed'),
          t('usersInvitedFailedDescription', { email }),
          'error'
        );
      })
    )) {
      if (!res) {
        continue;
      }
      resetInvitationModal(true);
    }
  };

  /** Get name for multiselect option from identity */
  const getNameFromSuggestion = (identity: IdentityBody) => {
    const { firstName, lastName, email } = identity;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    else return firstName || lastName || email;
  };

  const hasPendingText = Boolean(pendingInput.value.trim());
  const hasPendingEmailError = hasPendingText && !pendingInput.valid;
  const hasCommittablePendingEmail = hasPendingText && pendingInput.valid;

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={() => resetInvitationModal(false)}
      modalTitle={t('inviteModal.title')}
      contentPadding={0}
      closeable
    >
      <TeContainer backgroundColor="background.primary" width={640} padding={['m', 's']} fill>
        <TeForm name="newInvitationsForm" tesubmit={handleFormSubmit}>
          <TeContainerSlim gap="l" column grow>
            <TeContainerSlim gap="m" column grow>
              <TeContainerSlim gap="xs" column fill>
                <TeLabel text={t('inviteModal.emailLabel')} />
                <TeMultiselect
                  ref={multiselectRef}
                  teoptionadd={handleInviteEmailAdd}
                  tependingchange={handlePendingChange}
                  triggerKeys=","
                  customOptionRegex={EMAIL_PATTERN}
                  techange={(event) => setUsersToInvite(event.values)}
                  placeholder={t('inviteModal.emailPlaceholder')}
                  name="email-input"
                  grow
                  enableOptionAdding
                  commitOnBlur
                  fixedPosition
                  size="s"
                  caption={t('inviteModal.emailCaption')}
                  status={hasPendingEmailError ? 'error' : 'neutral'}
                  valid={!hasPendingEmailError}
                  statusText={hasPendingEmailError ? t('inviteModal.invalidEmail') : undefined}
                >
                  {shouldShowSuggestions &&
                    suggestions?.map((suggestion) => {
                      return (
                        <TeMultiselectOption
                          key={suggestion.id}
                          icon="account_circle"
                          iconColor="icon.secondary"
                          text={getNameFromSuggestion(suggestion)}
                          subtext={suggestion.email}
                          value={suggestion.email}
                          selected={usersToInvite.includes(suggestion.email)}
                        />
                      );
                    })}
                  {invitationEmails.map((email) => {
                    return (
                      <TeMultiselectExtraOption
                        key={email}
                        value={email}
                        techange={handleInviteEmailRemove}
                      />
                    );
                  })}
                </TeMultiselect>
              </TeContainerSlim>

              <TeDivider dividerColor="border.secondary" />

              <TeContainerSlim gap="s" column fill>
                <TeContainerSlim justifyContent="space-between" alignItems="center" fill>
                  <TeLabel text={t('inviteModal.profileLabel')} />
                  <TeContainerSlim gap="xs2" alignItems="center">
                    <TeIcon name="info" color="icon.secondary" />
                    <TeText color="text.secondary" size={12}>
                      {t('inviteModal.profileHelp')}
                    </TeText>
                  </TeContainerSlim>
                </TeContainerSlim>
                <ProfileSelector
                  profiles={invitationProfiles}
                  setProfiles={setInvitationProfiles}
                />
              </TeContainerSlim>
            </TeContainerSlim>
          </TeContainerSlim>
        </TeForm>
      </TeContainer>

      <TeButton
        slot="secondary"
        text={t('cancel')}
        variant="neutral"
        teclick={() => resetInvitationModal(false)}
        size="s"
      />
      <TeButton
        slot="primary"
        text={t('inviteModal.send')}
        variant="primary"
        teclick={() => submitFormWithName('newInvitationsForm')}
        loading={isInvitePending}
        disabled={
          isInvitePending ||
          (invitationEmails.length === 0 &&
            usersToInvite.length === 0 &&
            !hasCommittablePendingEmail) ||
          invitationProfiles.length === 0
        }
        size="s"
      />
    </TeModal>
  );
}

export default InvitationModal;
